import { Card, Typography, Chip, Stack, LinearProgress } from "@mui/material";
import { Box } from "@mui/system";
import { styled } from "@mui/material/styles";
import { linearProgressClasses } from "@mui/material/LinearProgress";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";

// ─────────────────────────────────────────────────────────────────────────────

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: spacing.Spacing.md.$value,
  borderRadius: spacing.Radius.xxs.$value,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: colors.Brand[500].$value,
    ...theme.applyStyles("dark", {
      backgroundColor: colors.Brand[500].$value,
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: spacing.Radius.xxs.$value,
    backgroundColor: colors.Brand[800].$value,
    ...theme.applyStyles("dark", {
      backgroundColor: colors.Brand[800].$value,
    }),
  },
}));

type StatusColor = "success" | "error";

interface PerformanceItemCardProps {
  website_name: string;
  website_id: string;
  totalIncome: string;
  cpa: string;
  cpaPercentage: string;
  cpaColor: StatusColor;
  rs: string;
  rsPercentage: string;
  rsColor: StatusColor;
  reg: string;
  click_out: string;
  ftd: string;
  qftd: string;
}

export function FraudPlayersCard({
  website_name,
  website_id,
  totalIncome,
  cpa,
  cpaPercentage,
  cpaColor,
  rs,
  rsPercentage,
  rsColor,
  ftd,
  qftd,
}: PerformanceItemCardProps) {
  return (
    <>
      <Card
        sx={{
          p: 1,
          borderRadius: spacing.Radius.lg.$value,
          boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
          border: "1px solid" + colors.GrayLightMode[200].$value,
          mb: spacing.Spacing.none.$value,
          mt: spacing.Spacing.xl.$value + "!important",
        }}
      >
        {/* Top Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body1"
            sx={{
              color: colors.GrayLightMode[700].$value,
              fontSize: typography.Size.Text.xs.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              mb: spacing.Spacing.md.$value,
            }}
          >
            {website_name} ({website_id})
          </Typography>
        </Stack>

        <Stack sx={{ mb: spacing.Spacing.xs.$value }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: typography.Size.Text.sm.$value,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            Total Income
            <Box
              component="span"
              sx={{
                fontSize: typography.Size.Text.xl.$value,
                fontWeight: typography.Font_weight.Medium.$value,
                ml: spacing.Spacing.xs.$value,
              }}
            >
              {totalIncome}
            </Box>
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: spacing.Spacing.xxs.$value }}
        >
          <Box display="flex" alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                mr: spacing.Spacing.md.$value,
              }}
            >
              CPA
            </Typography>
            <Chip
              label={cpaPercentage}
              size="small"
              sx={{
                bgcolor:
                  cpaColor === "success"
                    ? colors.Brand[800].$value
                    : colors.Brand[500].$value,
                color: colors.Base.White.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            sx={{ width: spacing.Spacing["8xl"].$value }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                mr: 1,
              }}
            >
              RS
            </Typography>
            <Chip
              label={rsPercentage}
              size="small"
              sx={{
                bgcolor:
                  rsColor === "success"
                    ? colors.Brand[800].$value
                    : colors.Brand[500].$value,
                color: colors.Base.White.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                fontWeight: typography.Font_weight.Bold.$value,
              }}
            >
              {cpa}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            sx={{ width: spacing.Spacing["8xl"].$value }}
          >
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            >
              {rs}
            </Typography>
          </Box>
        </Stack>

        {/* Progress Bar */}
        <BorderLinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, parseInt(cpaPercentage)))}
          sx={{ my: 0.5 }}
        />

        <Stack
          direction="row"
          justifyContent="space-between"
          mt={spacing.Spacing.md.$value}
          mb={spacing.Spacing.md.$value}
        >
          <Box
            sx={{
              width: "50%",
              pr: spacing.Spacing.md.$value,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontSize: typography.Size.Text.xs.$value,
              }}
            >
              QFTD
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  color: colors.GrayLightMode[900].$value,
                  fontSize: typography.Size.Text.sm.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  pr: spacing.Spacing.xs.$value,
                }}
              >
                {qftd}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: "50%",
              borderLeft: "1px solid" + colors.GrayLightMode[200].$value,
              pl: spacing.Spacing.md.$value,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontSize: typography.Size.Text.xs.$value,
              }}
            >
              FTD
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  color: colors.GrayLightMode[900].$value,
                  fontSize: typography.Size.Text.sm.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  pr: spacing.Spacing.xs.$value,
                }}
              >
                {ftd}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Card>
    </>
  );
}
export default FraudPlayersCard;
